import type React from "react"
import { useState, useRef, useEffect, useMemo } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { format, parse, isBefore, startOfDay, parseISO } from "date-fns"
import { toast } from "sonner"
import { DatePicker } from "../components/date-picker"
import { JobPreviewModal } from "../components/job-preview-modal"
import { AdditionalRequirementsSection } from "../components/CreateJob/AdditionalRequirementsSection"
import { AreasOfExpertiseSection } from "../components/CreateJob/AreasOfExpertiseSection"
import { CertificationRequirementsSection } from "../components/CreateJob/CertificationRequirementsSection"
import { CreateJobFooter } from "../components/CreateJob/CreateJobFooter"
import { EducationalRequirementsSection } from "../components/CreateJob/EducationalRequirementsSection"
import { FormField, MultiSelect, SectionHeader } from "../components/CreateJob/CreateJobFields"
import { GeneralInformationSection } from "../components/CreateJob/GeneralInformationSection"
import { CreateJobTextModal } from "../components/CreateJob/CreateJobTextModal"
import { InternalInformationSection } from "../components/CreateJob/InternalInformationSection"
import { LanguageRequirementsSection } from "../components/CreateJob/LanguageRequirementsSection"
import { ManagementExperienceSection } from "../components/CreateJob/ManagementExperienceSection"
import { useAppSelector, useAppDispatch } from "../app/hooks"
import {
    fetchFiltersDepartments,
    fetchHiringManagers,
    fetchJobLevels,
    fetchRegions,
    fetchCountries,
    fetchNationalityCountries,
    fetchCities,
    fetchEmploymentTypes,
    fetchWorkplaceTypes,
    fetchIndustries,
    fetchFieldsOfExpertise,
    fetchKeySkills,
    fetchLanguages,
    fetchSpeakingLevels,
    fetchReadingWritingLevels,
    fetchQualifications,
    fetchFieldsOfStudy,
    fetchCertificates,
    fetchGenders,
    fetchVisaStatuses,
    fetchDrivingLicenses,
    fetchManagementSkills,
    clearHiringManagers,
    clearCountries,
    clearCities,
} from "../features/filters/filterSlice"
import { fetchCompanyMember } from "../features/auth/authAPI"
import { ensureCurrentUser } from "../features/auth/authSlice"
import {
    createCertificateSeed,
    createFieldOfStudySeed,
    createJob,
    runJobMatches,
    buildJobHardFiltersPayload,
    hasAnyJobHardFilterSelected,
    applyJobHardFilters,
    fetchJobDetail,
    updateJob,
    fetchJobSummary,
} from "../features/jobs/jobAPI"

const CreateJob = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams<{ id: string }>()
    const isEditMode = !!id
    const cloneJobId =
        !isEditMode &&
        typeof (location.state as { cloneJobId?: unknown } | null)?.cloneJobId === "number"
            ? ((location.state as { cloneJobId?: number }).cloneJobId ?? null)
            : null
    const isPrefillMode = isEditMode || cloneJobId !== null
    const [isLoadingJob, setIsLoadingJob] = useState(false)
    const [openCalendar, setOpenCalendar] = useState<"opening" | "closing" | null>(null)
    const hiringManagerIdFromAPI = useRef<number | null>(null) // Store hiring manager ID from API
    const countryIdFromAPI = useRef<number | null>(null) // Store country ID from API
    const cityIdFromAPI = useRef<number | null>(null) // Store city ID from API
    const nationalityIdFromAPI = useRef<number | null>(null)
    const dispatch = useAppDispatch()
    const user = useAppSelector((state) => state.auth.user)
    const accessToken = useAppSelector((state) => state.auth.accessToken)
    const departments = useAppSelector((state) => state.filters.departments)
    const departmentsStatus = useAppSelector((state) => state.filters.departmentsStatus)
    const hiringManagers = useAppSelector((state) => state.filters.hiringManagers)
    const hiringManagersStatus = useAppSelector((state) => state.filters.hiringManagersStatus)
    const jobLevels = useAppSelector((state) => state.filters.jobLevels)
    const jobLevelsStatus = useAppSelector((state) => state.filters.jobLevelsStatus)
    const regions = useAppSelector((state) => state.filters.regions)
    const regionsStatus = useAppSelector((state) => state.filters.regionsStatus)
    const countries = useAppSelector((state) => state.filters.countries)
    const countriesStatus = useAppSelector((state) => state.filters.countriesStatus)
    const nationalityCountries = useAppSelector((state) => state.filters.nationalityCountries)
    const nationalityCountriesStatus = useAppSelector(
        (state) => state.filters.nationalityCountriesStatus
    )
    const cities = useAppSelector((state) => state.filters.cities)
    const citiesStatus = useAppSelector((state) => state.filters.citiesStatus)
    const employmentTypes = useAppSelector((state) => state.filters.employmentTypes)
    const employmentTypesStatus = useAppSelector((state) => state.filters.employmentTypesStatus)
    const workplaceTypes = useAppSelector((state) => state.filters.workplaceTypes)
    const workplaceTypesStatus = useAppSelector((state) => state.filters.workplaceTypesStatus)
    const industries = useAppSelector((state) => state.filters.industries)
    const industriesStatus = useAppSelector((state) => state.filters.industriesStatus)
    const fieldsOfExpertise = useAppSelector((state) => state.filters.fieldsOfExpertise)
    const fieldsOfExpertiseStatus = useAppSelector((state) => state.filters.fieldsOfExpertiseStatus)
    const languages = useAppSelector((state) => state.filters.languages)
    const languagesStatus = useAppSelector((state) => state.filters.languagesStatus)
    const speakingLevels = useAppSelector((state) => state.filters.speakingLevels)
    const speakingLevelsStatus = useAppSelector((state) => state.filters.speakingLevelsStatus)
    const readingWritingLevels = useAppSelector((state) => state.filters.readingWritingLevels)
    const readingWritingLevelsStatus = useAppSelector((state) => state.filters.readingWritingLevelsStatus)
    const qualifications = useAppSelector((state) => state.filters.qualifications)
    const qualificationsStatus = useAppSelector((state) => state.filters.qualificationsStatus)
    const fieldsOfStudy = useAppSelector((state) => state.filters.fieldsOfStudy)
    const fieldsOfStudyStatus = useAppSelector((state) => state.filters.fieldsOfStudyStatus)
    const certificates = useAppSelector((state) => state.filters.certificates)
    const certificatesStatus = useAppSelector((state) => state.filters.certificatesStatus)
    const genders = useAppSelector((state) => state.filters.genders)
    const gendersStatus = useAppSelector((state) => state.filters.gendersStatus)
    const visaStatuses = useAppSelector((state) => state.filters.visaStatuses)
    const visaStatusesStatus = useAppSelector((state) => state.filters.visaStatusesStatus)
    const drivingLicenses = useAppSelector((state) => state.filters.drivingLicenses)
    const drivingLicensesStatus = useAppSelector((state) => state.filters.drivingLicensesStatus)
    const managementSkills = useAppSelector((state) => state.filters.managementSkills)
    const managementSkillsStatus = useAppSelector((state) => state.filters.managementSkillsStatus)

    const [tasks, setTasks] = useState("")
    const [isGeneratingJobDescription, setIsGeneratingJobDescription] = useState(false)
    const [hasGeneratedJobDescription, setHasGeneratedJobDescription] = useState(false)
    const [canEditJobDescription, setCanEditJobDescription] = useState(false)
    const [lastJobDescriptionSourceText, setLastJobDescriptionSourceText] = useState("")
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [isCreatingJob, setIsCreatingJob] = useState(false)
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null)
    const [selectedHiringManagerId, setSelectedHiringManagerId] = useState<number | null>(null)
    const [selectedJobLevelId, setSelectedJobLevelId] = useState<number | null>(null)
    const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null)
    const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null)
    const [selectedCityId, setSelectedCityId] = useState<number | null>(null)
    const [selectedEmploymentTypeId, setSelectedEmploymentTypeId] = useState<string | null>(null)
    const [selectedWorkplaceTypeId, setSelectedWorkplaceTypeId] = useState<string | null>(null)
    const [selectedNationalityId, setSelectedNationalityId] = useState<number | null>(null)
    const [areasOfExpertise, setAreasOfExpertise] = useState<Array<{
        fieldOfExpertiseId: number | null;
        experienceLevel: number | null;
        keySkillsIds: number[];
        niceSkillsIds: number[];
    }>>([{ fieldOfExpertiseId: null, experienceLevel: null, keySkillsIds: [], niceSkillsIds: [] }])
    const [educationRequirements, setEducationRequirements] = useState<Array<{
        qualificationId: number | null;
        fieldOfStudyId: number | null;
        fieldOfStudyOtherName: string | null;
    }>>([{ qualificationId: null, fieldOfStudyId: null, fieldOfStudyOtherName: null }])
    const [companyId, setCompanyId] = useState<number | null>(null)
    const [formData, setFormData] = useState({
        jobTitle: "",
        companyName: "",
        city: "",
        workplaceType: "",
        openingDate: format(new Date(), "d MMM yyyy"),
        closingDate: "",
    })

    const [industryExp, setIndustryExp] = useState<number[]>([])
    // Store key skills per field of expertise ID
    const [keySkillsByExpertise, setKeySkillsByExpertise] = useState<Record<string, Array<{ id: number; name: string }>>>({})
    const [keySkillsLoadingByExpertise, setKeySkillsLoadingByExpertise] = useState<Record<string, boolean>>({})

    const [languageRequirements, setLanguageRequirements] = useState<Array<{
        languageId: number | null;
        speakingLevelId: string | null;
        readingWritingLevelId: string | null;
    }>>([{ languageId: null, speakingLevelId: null, readingWritingLevelId: null }])
    const [certificationIds, setCertificationIds] = useState<number[]>([])
    const [customCertificationNames, setCustomCertificationNames] = useState<string[]>([])
    const [isOtherCertificationModalOpen, setIsOtherCertificationModalOpen] = useState(false)
    const [otherCertificationName, setOtherCertificationName] = useState("")
    const [genderValue, setGenderValue] = useState<string | null>(null)
    const [visaStatusValue, setVisaStatusValue] = useState<string | null>(null)
    const [drivingLicenseValue, setDrivingLicenseValue] = useState<string | null>(null)
    const [managementExperienceValue, setManagementExperienceValue] = useState<"YES" | "NO" | null>(null)
    const [peopleManagedRangeValue, setPeopleManagedRangeValue] = useState<string | null>(null)
    const [managementYearsOfExperience, setManagementYearsOfExperience] = useState<number>(0)
    const otherCertificationOption = "Other"
    const otherFieldOfStudyOption = { id: "__OTHER_FIELD_OF_STUDY__", name: "Other" }
    const [fieldOfStudyModalIndex, setFieldOfStudyModalIndex] = useState<number | null>(null)
    const [otherFieldOfStudyName, setOtherFieldOfStudyName] = useState("")
    const managementExperienceOptions = [
        { id: "YES", name: "YES" },
        { id: "NO", name: "NO" },
    ]
    const peopleManagedRangeOptions = useMemo(
        () => managementSkills.map((skill) => ({ id: String(skill.id), name: skill.name })),
        [managementSkills]
    )

    // Fetch company_id: First get company_member_id from /api/settings/users/me/, then fetch company member to get company_id
    useEffect(() => {
        if (accessToken) {
            const loadCompanyId = async () => {
                try {
                    let companyMemberId = user?.company_member_id;

                    if (!companyMemberId) {
                        const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
                        companyMemberId = ensuredUser.company_member_id as number | undefined;
                    }

                    if (companyMemberId) {
                        const companyMemberResponse = await fetchCompanyMember(companyMemberId, accessToken);
                        if (companyMemberResponse.data?.company?.id) {
                            setCompanyId(companyMemberResponse.data.company.id);
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch company data:", error);
                }
            };
            loadCompanyId();
        }
    }, [user?.company_member_id, accessToken, dispatch])

    const populateFormFromJobData = (jobData: any, includeDates: boolean) => {
        setFormData({
            jobTitle: jobData.job_title || "",
            companyName: "",
            city: jobData.city?.name || "",
            workplaceType:
                Array.isArray(jobData.workplace_type) && jobData.workplace_type.length > 0
                    ? jobData.workplace_type[0].name
                    : "",
            openingDate:
                includeDates && jobData.opening_date
                    ? format(parseISO(jobData.opening_date), "d MMM yyyy")
                    : format(new Date(), "d MMM yyyy"),
            closingDate:
                includeDates && jobData.closing_date
                    ? format(parseISO(jobData.closing_date), "d MMM yyyy")
                    : "",
        })

        setTasks(jobData.description || "")

        setSelectedHiringManagerId(null)
        setSelectedCountryId(null)
        setSelectedCityId(null)
        setSelectedNationalityId(null)

        const departmentId = jobData.department?.id || null
        hiringManagerIdFromAPI.current = jobData.hiring_manager?.id || null
        setSelectedDepartmentId(departmentId)

        countryIdFromAPI.current = jobData.country?.id || null
        cityIdFromAPI.current = jobData.city?.id || null
        const rawNationality = jobData.nationality
        if (Array.isArray(rawNationality) && rawNationality.length > 0) {
            const first = rawNationality[0]
            nationalityIdFromAPI.current =
                typeof first === "object" && first !== null && "id" in first
                    ? (first as { id: number }).id
                    : typeof first === "number"
                        ? first
                        : null
        } else if (rawNationality && typeof rawNationality === "object" && "id" in rawNationality) {
            nationalityIdFromAPI.current = (rawNationality as { id: number }).id
        } else {
            nationalityIdFromAPI.current = null
        }
        setSelectedRegionId(jobData.region?.id || null)

        setSelectedJobLevelId(jobData.job_level?.id || null)
        setSelectedEmploymentTypeId(
            Array.isArray(jobData.employment_type) && jobData.employment_type.length > 0
                ? String(jobData.employment_type[0].id)
                : null
        )
        setSelectedWorkplaceTypeId(
            Array.isArray(jobData.workplace_type) && jobData.workplace_type.length > 0
                ? String(jobData.workplace_type[0].id)
                : null
        )

        const areas =
            Array.isArray(jobData.areas_of_expertise) && jobData.areas_of_expertise.length > 0
                ? jobData.areas_of_expertise.map((area: any) => ({
                    fieldOfExpertiseId: area.field_of_expertise?.id || null,
                    experienceLevel: area.years_in_this_area_of_expertise || null,
                    keySkillsIds: Array.isArray(area.skills_must_have)
                        ? area.skills_must_have.map((s: any) => s.id)
                        : [],
                    niceSkillsIds: Array.isArray(area.skills_nice_to_have)
                        ? area.skills_nice_to_have.map((s: any) => s.id)
                        : [],
                }))
                : [{ fieldOfExpertiseId: null, experienceLevel: null, keySkillsIds: [], niceSkillsIds: [] }]
        setAreasOfExpertise(areas)

        const education =
            Array.isArray(jobData.education) && jobData.education.length > 0
                ? jobData.education.map((edu: any) => ({
                    qualificationId: edu.qualification?.id || null,
                    fieldOfStudyId: edu.field_of_study?.id || null,
                    fieldOfStudyOtherName: edu.other_field_of_study || null,
                }))
                : [{ qualificationId: null, fieldOfStudyId: null, fieldOfStudyOtherName: null }]
        setEducationRequirements(education)

        const languages =
            Array.isArray(jobData.language_skills) && jobData.language_skills.length > 0
                ? jobData.language_skills.map((lang: any) => ({
                    languageId: lang.language?.id || null,
                    speakingLevelId: lang.speaking_level || null,
                    readingWritingLevelId: lang.reading_writing_level || null,
                }))
                : [{ languageId: null, speakingLevelId: null, readingWritingLevelId: null }]
        setLanguageRequirements(languages)

        setCertificationIds(
            Array.isArray(jobData.certificate) && jobData.certificate.length > 0
                ? jobData.certificate.map((cert: any) => cert.id)
                : []
        )
        setCustomCertificationNames(
            Array.isArray((jobData as any).other_certificates) ? (jobData as any).other_certificates : []
        )

        setIndustryExp(
            Array.isArray(jobData.industry) && jobData.industry.length > 0
                ? jobData.industry.map((ind: any) => ind.id)
                : []
        )

        setDrivingLicenseValue(jobData.driving_license || null)
        setVisaStatusValue(jobData.visa_status || null)

        const jobManagementSkills = Array.isArray((jobData as any).management_skills)
            ? (jobData as any).management_skills
            : []
        if (jobManagementSkills.length > 0) {
            const firstSkill = jobManagementSkills[0]
            setManagementExperienceValue("YES")
            if (firstSkill?.management_skill?.id != null) {
                setPeopleManagedRangeValue(String(firstSkill.management_skill.id))
            } else {
                setPeopleManagedRangeValue(null)
            }
            setManagementYearsOfExperience(
                typeof firstSkill?.years_of_experience === "number"
                    ? firstSkill.years_of_experience
                    : 0
            )
        } else {
            setManagementExperienceValue(
                typeof (jobData as any).manages_people === "boolean"
                    ? ((jobData as any).manages_people ? "YES" : "NO")
                    : null
            )
            const rawPm = (jobData as any).people_managed_range
            let peopleManagedRange: string | null = null
            if (rawPm !== null && rawPm !== undefined && rawPm !== "") {
                if (typeof rawPm === "object" && rawPm !== null && "id" in rawPm) {
                    peopleManagedRange = String((rawPm as { id: number }).id)
                } else {
                    peopleManagedRange = String(rawPm)
                }
            }
            setPeopleManagedRangeValue(peopleManagedRange)
            setManagementYearsOfExperience(0)
        }

        setGenderValue(jobData.gender || null)
    }

    // Load job data for editing or cloning
    useEffect(() => {
        const sourceJobId = isEditMode ? Number(id) : cloneJobId
        if (accessToken && sourceJobId) {
            const loadJobData = async () => {
                try {
                    setIsLoadingJob(true)
                    if (isNaN(sourceJobId)) {
                        toast.error("Invalid job ID")
                        navigate("/all-jobs")
                        return
                    }

                    const response = await fetchJobDetail(sourceJobId, accessToken)
                    if (response.error || !response.data) {
                        toast.error(response.error || "Failed to load job data")
                        navigate("/all-jobs")
                        return
                    }

                    populateFormFromJobData(response.data, isEditMode)

                } catch (error) {
                    console.error("Failed to load job data:", error)
                    toast.error("Failed to load job data")
                    navigate("/all-jobs")
                } finally {
                    setIsLoadingJob(false)
                }
            }

            loadJobData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, id, cloneJobId, accessToken])

    // Fetch departments, job levels, regions, employment types, workplace types, industries, fields of expertise, languages, levels, education, and other filters on mount
    useEffect(() => {
        dispatch(fetchFiltersDepartments())
        dispatch(fetchJobLevels())
        dispatch(fetchRegions())
        dispatch(fetchEmploymentTypes())
        dispatch(fetchWorkplaceTypes())
        dispatch(fetchIndustries())
        dispatch(fetchFieldsOfExpertise())
        dispatch(fetchLanguages())
        dispatch(fetchSpeakingLevels())
        dispatch(fetchReadingWritingLevels())
        dispatch(fetchQualifications())
        dispatch(fetchFieldsOfStudy())
        dispatch(fetchCertificates())
        dispatch(fetchGenders())
        dispatch(fetchVisaStatuses())
        dispatch(fetchDrivingLicenses())
        dispatch(fetchManagementSkills())
        dispatch(fetchNationalityCountries())
    }, [dispatch])

    // Fetch hiring managers when department is selected
    useEffect(() => {
        if (selectedDepartmentId && companyId && accessToken) {
            dispatch(
                fetchHiringManagers({
                    departmentId: selectedDepartmentId,
                    companyId,
                    accessToken,
                })
            )
        } else {
            dispatch(clearHiringManagers())
            setSelectedHiringManagerId(null)
            // Only clear the ref if we're not prefilling or if department is actually cleared
            if (!isPrefillMode || !selectedDepartmentId) {
                hiringManagerIdFromAPI.current = null
            }
        }
    }, [selectedDepartmentId, companyId, accessToken, dispatch, isPrefillMode])

    // Set hiring manager ID once hiring managers are loaded (for edit mode)
    useEffect(() => {
        if (
            isPrefillMode &&
            hiringManagersStatus === "succeeded" &&
            hiringManagers.length > 0 &&
            hiringManagerIdFromAPI.current !== null
        ) {
            // Check if the hiring manager ID from API exists in the loaded list
            const managerExists = hiringManagers.some(
                (manager) => manager.id === hiringManagerIdFromAPI.current
            )
            if (managerExists) {
                // Only set if it's different from current value to avoid unnecessary updates
                if (selectedHiringManagerId !== hiringManagerIdFromAPI.current) {
                    console.log("Setting hiring manager ID:", hiringManagerIdFromAPI.current)
                    setSelectedHiringManagerId(hiringManagerIdFromAPI.current)
                }
            } else {
                console.warn("Hiring manager ID from API not found in loaded list:", hiringManagerIdFromAPI.current)
            }
        }
    }, [hiringManagersStatus, hiringManagers, isPrefillMode, selectedHiringManagerId])

    // Fetch countries when region is selected
    useEffect(() => {
        if (selectedRegionId) {
            dispatch(fetchCountries({ regionId: selectedRegionId }))
        } else {
            dispatch(clearCountries())
            setSelectedCountryId(null)
            setSelectedCityId(null)
            // Only clear the refs if we're not prefilling or if region is actually cleared
            if (!isPrefillMode || !selectedRegionId) {
                countryIdFromAPI.current = null
                cityIdFromAPI.current = null
            }
        }
    }, [selectedRegionId, dispatch, isPrefillMode])

    // Set nationality ID once nationality countries are loaded (for edit/clone prefill)
    useEffect(() => {
        if (
            isPrefillMode &&
            nationalityCountriesStatus === "succeeded" &&
            nationalityCountries.length > 0 &&
            nationalityIdFromAPI.current !== null
        ) {
            const nationalityExists = nationalityCountries.some(
                (country) => country.id === nationalityIdFromAPI.current
            )
            if (nationalityExists) {
                setSelectedNationalityId(nationalityIdFromAPI.current)
            }
        }
    }, [nationalityCountriesStatus, nationalityCountries, isPrefillMode])

    // Set country ID once countries are loaded (for edit mode)
    useEffect(() => {
        if (
            isPrefillMode &&
            countriesStatus === "succeeded" &&
            countries.length > 0 &&
            countryIdFromAPI.current !== null
        ) {
            // Check if the country ID from API exists in the loaded list
            const countryExists = countries.some(
                (country) => country.id === countryIdFromAPI.current
            )
            if (countryExists) {
                // Always set it to ensure it's displayed (even if it's the same value)
                console.log("Setting country ID:", countryIdFromAPI.current, "from countries:", countries.map(c => c.id))
                setSelectedCountryId(countryIdFromAPI.current)
            } else {
                console.warn("Country ID from API not found in loaded list:", countryIdFromAPI.current, "Available countries:", countries.map(c => ({ id: c.id, name: c.name })))
            }
        }
    }, [countriesStatus, countries, isPrefillMode])

    // Fetch cities when country is selected
    useEffect(() => {
        if (selectedCountryId) {
            dispatch(fetchCities({ countryId: selectedCountryId }))
        } else {
            dispatch(clearCities())
            setSelectedCityId(null)
            // Only clear the city ref if we're not prefilling or if country is actually cleared
            if (!isPrefillMode || !selectedCountryId) {
                cityIdFromAPI.current = null
            }
        }
    }, [selectedCountryId, dispatch, isPrefillMode])

    // Set city ID once cities are loaded (for edit mode)
    useEffect(() => {
        if (
            isPrefillMode &&
            citiesStatus === "succeeded" &&
            cities.length > 0 &&
            cityIdFromAPI.current !== null
        ) {
            // Check if the city ID from API exists in the loaded list
            const cityExists = cities.some(
                (city) => city.id === cityIdFromAPI.current
            )
            if (cityExists) {
                // Always set it to ensure it's displayed (even if it's the same value)
                console.log("Setting city ID:", cityIdFromAPI.current, "from cities:", cities.map(c => c.id))
                setSelectedCityId(cityIdFromAPI.current)
            } else {
                console.warn("City ID from API not found in loaded list:", cityIdFromAPI.current, "Available cities:", cities.map(c => ({ id: c.id, name: c.name })))
            }
        }
    }, [citiesStatus, cities, isPrefillMode])

    // Fetch key skills for each selected field of expertise
    useEffect(() => {
        areasOfExpertise.forEach((area) => {
            if (area.fieldOfExpertiseId) {
                const expertiseIdStr = String(area.fieldOfExpertiseId)
                // Only fetch if not cached and not currently loading
                const isCached = keySkillsByExpertise[expertiseIdStr] !== undefined
                const isLoading = keySkillsLoadingByExpertise[expertiseIdStr] === true

                if (!isCached && !isLoading) {
                    // Set loading state
                    setKeySkillsLoadingByExpertise((prev) => ({
                        ...prev,
                        [expertiseIdStr]: true,
                    }))

                    // Fetch key skills for this field of expertise
                    dispatch(fetchKeySkills({ fieldOfExpertiseId: area.fieldOfExpertiseId }))
                        .then((result) => {
                            if (fetchKeySkills.fulfilled.match(result)) {
                                setKeySkillsByExpertise((prev) => ({
                                    ...prev,
                                    [expertiseIdStr]: result.payload,
                                }))
                            }
                        })
                        .catch((error) => {
                            console.error("Failed to fetch key skills:", error)
                        })
                        .finally(() => {
                            setKeySkillsLoadingByExpertise((prev) => ({
                                ...prev,
                                [expertiseIdStr]: false,
                            }))
                        })
                }
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areasOfExpertise.map(a => a.fieldOfExpertiseId).join(','), dispatch])

    // Clear invalid experience levels when job level changes
    useEffect(() => {
        if (selectedJobLevelId) {
            const validOptions = getExperienceLevelOptions()
            const validValues = new Set(validOptions.map(opt => {
                // Handle "+" values - extract the number part
                if (opt.id.endsWith("+")) {
                    return parseInt(opt.id.replace("+", ""), 10)
                }
                return parseInt(opt.id, 10)
            }))
            // Also allow 0 for internship
            validValues.add(0)

            setAreasOfExpertise((prev) => {
                return prev.map((area) => {
                    if (area.experienceLevel !== null && area.experienceLevel !== undefined && !validValues.has(area.experienceLevel)) {
                        return { ...area, experienceLevel: null }
                    }
                    return area
                })
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJobLevelId, jobLevels])

    const toggleIndustryItem = (industryId: number) => {
        if (industryExp.includes(industryId)) {
            setIndustryExp(industryExp.filter((id) => id !== industryId))
        } else {
            setIndustryExp([...industryExp, industryId])
        }
    }

    const toggleKeySkill = (index: number, skillId: number) => {
        // Prevent selecting a skill as key if it is already selected as nice-to-have.
        const isAlreadyNiceSkill = areasOfExpertise.some((area) => area.niceSkillsIds.includes(skillId))
        if (isAlreadyNiceSkill) return

        setAreasOfExpertise((prev) => {
            const updated = [...prev]
            const currentSkills = updated[index].keySkillsIds
            if (currentSkills.includes(skillId)) {
                updated[index].keySkillsIds = currentSkills.filter((id) => id !== skillId)
            } else {
                updated[index].keySkillsIds = [...currentSkills, skillId]
            }
            return updated
        })
    }

    const toggleNiceSkill = (index: number, skillId: number) => {
        // Prevent selecting a skill as nice-to-have if it is already selected as a key skill.
        const isAlreadyKeySkill = areasOfExpertise.some((area) => area.keySkillsIds.includes(skillId))
        if (isAlreadyKeySkill) return

        setAreasOfExpertise((prev) => {
            const updated = [...prev]
            const currentSkills = updated[index].niceSkillsIds
            if (currentSkills.includes(skillId)) {
                updated[index].niceSkillsIds = currentSkills.filter((id) => id !== skillId)
            } else {
                updated[index].niceSkillsIds = [...currentSkills, skillId]
            }
            return updated
        })
    }

    // Keep key skills unique across all expertise blocks.
    const globalKeySkillIds = useMemo(() => {
        const ids = new Set<number>()
        areasOfExpertise.forEach((area) => {
            area.keySkillsIds.forEach((id) => ids.add(id))
        })
        return ids
    }, [areasOfExpertise])

    // Track all nice-to-have skills across expertise blocks.
    const globalNiceSkillIds = useMemo(() => {
        const ids = new Set<number>()
        areasOfExpertise.forEach((area) => {
            area.niceSkillsIds.forEach((id) => ids.add(id))
        })
        return ids
    }, [areasOfExpertise])

    // If a skill is marked as a key skill, remove it from nice-to-have selections everywhere.
    useEffect(() => {
        setAreasOfExpertise((prev) => {
            let hasChanges = false
            const updated = prev.map((area) => {
                const filteredNiceSkills = area.niceSkillsIds.filter((id) => !globalKeySkillIds.has(id))
                if (filteredNiceSkills.length !== area.niceSkillsIds.length) {
                    hasChanges = true
                    return { ...area, niceSkillsIds: filteredNiceSkills }
                }
                return area
            })
            return hasChanges ? updated : prev
        })
    }, [globalKeySkillIds])

    // If a skill is marked as nice-to-have, remove it from key skill selections everywhere.
    useEffect(() => {
        setAreasOfExpertise((prev) => {
            let hasChanges = false
            const updated = prev.map((area) => {
                const filteredKeySkills = area.keySkillsIds.filter((id) => !globalNiceSkillIds.has(id))
                if (filteredKeySkills.length !== area.keySkillsIds.length) {
                    hasChanges = true
                    return { ...area, keySkillsIds: filteredKeySkills }
                }
                return area
            })
            return hasChanges ? updated : prev
        })
    }, [globalNiceSkillIds])

    // Generate experience level options based on selected job level
    const getExperienceLevelOptions = (): Array<{ id: string; name: string }> => {
        if (!selectedJobLevelId) {
            // Default: return 1-12 if no job level is selected
            return [...Array(12)].map((_, i) => ({ id: String(i + 1), name: String(i + 1) }))
        }

        const selectedJobLevel = jobLevels.find((level) => level.id === selectedJobLevelId)
        if (!selectedJobLevel) {
            return [...Array(12)].map((_, i) => ({ id: String(i + 1), name: String(i + 1) }))
        }

        const jobLevelName = selectedJobLevel.name.toLowerCase().trim()

        // Check if it's internship
        if (jobLevelName.includes("internship")) {
            return [{ id: "0", name: "0" }]
        }

        // Check if it has a "+" sign (e.g., "12+ years", "13+ years")
        const plusMatch = jobLevelName.match(/(\d+)\+/)
        if (plusMatch) {
            const options: Array<{ id: string; name: string }> = []
            // Show numbers from 1 to 50 for "+" job levels
            // For "12+ years", "13+ years", "14+ years", etc.: show 1-50
            for (let i = 1; i <= 50; i++) {
                options.push({ id: String(i), name: String(i) })
            }
            return options
        }

        // Check for range format like "1 to 4 years", "5 to 8 years", "9 to 12 years"
        const rangeMatch = jobLevelName.match(/(\d+)\s+to\s+(\d+)/)
        if (rangeMatch) {
            const maxYears = parseInt(rangeMatch[2], 10)
            const options: Array<{ id: string; name: string }> = []
            // Show from 1 to maxYears
            // For "1 to 4 years": show 1-4
            // For "5 to 8 years": show 1-8
            // For "9 to 12 years": show 1-12
            for (let i = 1; i <= maxYears; i++) {
                options.push({ id: String(i), name: String(i) })
            }
            return options
        }

        // Extract single number from job level name (e.g., "10", "5")
        const numberMatch = jobLevelName.match(/(\d+)/)
        if (numberMatch) {
            const maxYears = parseInt(numberMatch[1], 10)
            const options: Array<{ id: string; name: string }> = []
            for (let i = 1; i <= maxYears; i++) {
                options.push({ id: String(i), name: String(i) })
            }
            return options
        }

        // Default fallback
        return [...Array(12)].map((_, i) => ({ id: String(i + 1), name: String(i + 1) }))
    }

    const handleAreaOfExpertiseChange = (index: number, field: "fieldOfExpertiseId" | "experienceLevel", value: string) => {
        setAreasOfExpertise((prev) => {
            const updated = [...prev]
            let processedValue: number | null = null

            if (field === "experienceLevel") {
                if (value === "" || value === null) {
                    processedValue = null
                } else if (value === "0") {
                    processedValue = 0
                } else if (value.endsWith("+")) {
                    // For values like "12+", extract the number part
                    const numValue = parseInt(value.replace("+", ""), 10)
                    processedValue = isNaN(numValue) ? null : numValue
                } else {
                    const numValue = parseInt(value, 10)
                    processedValue = isNaN(numValue) ? null : numValue
                }
            } else {
                processedValue = value === "" ? null : Number(value)
            }

            updated[index] = {
                ...updated[index],
                [field]: processedValue,
                // Clear skills when field of expertise changes
                ...(field === "fieldOfExpertiseId" && { keySkillsIds: [], niceSkillsIds: [] })
            }
            return updated
        })
    }

    const addAreaOfExpertise = () => {
        setAreasOfExpertise((prev) => [
            ...prev,
            { fieldOfExpertiseId: null, experienceLevel: null, keySkillsIds: [], niceSkillsIds: [] },
        ])
    }

    const removeAreaOfExpertise = (index: number) => {
        setAreasOfExpertise((prev) => prev.filter((_, i) => i !== index))
    }

    const handleEducationRequirementChange = (index: number, field: "qualificationId" | "fieldOfStudyId", value: string) => {
        setEducationRequirements((prev) => {
            const updated = [...prev]
            updated[index] = {
                ...updated[index],
                [field]: value === "" ? null : Number(value),
                ...(field === "fieldOfStudyId" && { fieldOfStudyOtherName: null }),
            }
            return updated
        })
    }

    const handleFieldOfStudyChange = (index: number, value: string) => {
        if (value === otherFieldOfStudyOption.id) {
            setFieldOfStudyModalIndex(index)
            setOtherFieldOfStudyName("")
            return
        }

        if (educationRequirements[index]?.fieldOfStudyOtherName === value) {
            return
        }

        handleEducationRequirementChange(index, "fieldOfStudyId", value)
    }

    const handleAddOtherFieldOfStudy = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const trimmedName = otherFieldOfStudyName.trim()
        if (!trimmedName || fieldOfStudyModalIndex === null) return

        const existingFieldOfStudy = fieldsOfStudy.find(
            (field) => field.name.toLowerCase() === trimmedName.toLowerCase()
        )

        if (existingFieldOfStudy) {
            setEducationRequirements((prev) => {
                const updated = [...prev]
                if (!updated[fieldOfStudyModalIndex]) return updated

                updated[fieldOfStudyModalIndex] = {
                    ...updated[fieldOfStudyModalIndex],
                    fieldOfStudyId: existingFieldOfStudy.id,
                    fieldOfStudyOtherName: null,
                }

                return updated
            })
            setOtherFieldOfStudyName("")
            setFieldOfStudyModalIndex(null)
            return
        }

        if (!accessToken) {
            toast.error("You must be signed in to add a field of study.")
            return
        }

        try {
            const result = await createFieldOfStudySeed(trimmedName, accessToken)
            if (result.error || !result.data) {
                toast.error(result.error || "Failed to create field of study.")
                return
            }

            await dispatch(fetchFieldsOfStudy()).unwrap()
            setEducationRequirements((prev) => {
                const updated = [...prev]
                if (!updated[fieldOfStudyModalIndex]) return updated

                updated[fieldOfStudyModalIndex] = {
                    ...updated[fieldOfStudyModalIndex],
                    fieldOfStudyId: result.data!.id,
                    fieldOfStudyOtherName: null,
                }

                return updated
            })
            setOtherFieldOfStudyName("")
            setFieldOfStudyModalIndex(null)
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to create field of study."
            toast.error(message)
        }
    }

    const handleCloseOtherFieldOfStudyModal = () => {
        setOtherFieldOfStudyName("")
        setFieldOfStudyModalIndex(null)
    }

    const addEducationRequirement = () => {
        setEducationRequirements((prev) => [
            ...prev,
            { qualificationId: null, fieldOfStudyId: null, fieldOfStudyOtherName: null },
        ])
    }

    const removeEducationRequirement = (index: number) => {
        setEducationRequirements((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
    }

    const handleLanguageRequirementChange = (index: number, field: "languageId" | "speakingLevelId" | "readingWritingLevelId", value: string) => {
        setLanguageRequirements((prev) => {
            const updated = [...prev]
            updated[index] = {
                ...updated[index],
                [field]:
                    field === "languageId"
                        ? value === "" ? null : Number(value)
                        : value === "" ? null : value,
            }
            return updated
        })
    }

    const addLanguageRequirement = () => {
        setLanguageRequirements((prev) => [
            ...prev,
            { languageId: null, speakingLevelId: null, readingWritingLevelId: null },
        ])
    }

    const removeLanguageRequirement = (index: number) => {
        setLanguageRequirements((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
    }

    const handleGenderChange = (value: string) => {
        setGenderValue(value)
    }

    const handleVisaStatusChange = (value: string) => {
        setVisaStatusValue(value === "" ? null : value)
    }

    const handleDrivingLicenseChange = (value: string) => {
        setDrivingLicenseValue(value === "" ? null : value)
    }

    const handleManagementExperienceChange = (value: string) => {
        const nextValue = value === "YES" || value === "NO" ? value : null
        setManagementExperienceValue(nextValue)
        if (nextValue !== "YES") {
            setPeopleManagedRangeValue(null)
        }
    }

    const handlePeopleManagedRangeChange = (value: string) => {
        setPeopleManagedRangeValue(value === "" ? null : value)
    }

    const handleAddOtherCertification = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const trimmedName = otherCertificationName.trim()
        if (!trimmedName) return

        const existingCertificate = certificates.find(
            (certificate) => certificate.name.toLowerCase() === trimmedName.toLowerCase()
        )

        if (existingCertificate) {
            setCertificationIds((prev) =>
                prev.includes(existingCertificate.id) ? prev : [...prev, existingCertificate.id]
            )
            setOtherCertificationName("")
            setIsOtherCertificationModalOpen(false)
            return
        }

        if (!accessToken) {
            toast.error("You must be signed in to add a certificate.")
            return
        }

        try {
            const result = await createCertificateSeed(trimmedName, accessToken)
            if (result.error || !result.data) {
                toast.error(result.error || "Failed to create certificate.")
                return
            }

            await dispatch(fetchCertificates()).unwrap()
            setCertificationIds((prev) =>
                prev.includes(result.data!.id) ? prev : [...prev, result.data!.id]
            )
            setOtherCertificationName("")
            setIsOtherCertificationModalOpen(false)
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to create certificate."
            toast.error(message)
        }
    }

    const handleCloseOtherCertificationModal = () => {
        setOtherCertificationName("")
        setIsOtherCertificationModalOpen(false)
    }

    const formatSelectedNames = <T extends { id: number | string; name: string }>(
        ids: Array<number | string>,
        options: T[]
    ) =>
        ids
            .map((id) => options.find((option) => String(option.id) === String(id))?.name)
            .filter(Boolean)
            .join(", ")

    const buildJobDescriptionText = () => {
        const departmentName = departments.find((department) => department.id === selectedDepartmentId)?.name || ""
        const hiringManagerName = (() => {
            const manager = hiringManagers.find((item) => item.id === selectedHiringManagerId)
            return manager
                ? `${manager.first_name || ""} ${manager.middle_name || ""} ${manager.last_name || ""}`.trim()
                : ""
        })()
        const jobLevelName = jobLevels.find((level) => level.id === selectedJobLevelId)?.name || ""
        const regionName = regions.find((region) => region.id === selectedRegionId)?.name || ""
        const countryName = countries.find((country) => country.id === selectedCountryId)?.name || ""
        const cityName = cities.find((city) => city.id === selectedCityId)?.name || formData.city
        const employmentTypeName = employmentTypes.find((type) => String(type.id) === String(selectedEmploymentTypeId))?.name || ""
        const workplaceTypeName = workplaceTypes.find((type) => String(type.id) === String(selectedWorkplaceTypeId))?.name || selectedWorkplaceTypeName
        const industryNames = formatSelectedNames(industryExp, industries)
        const certificateNames = [
            ...certificationIds
                .map((id) => certificates.find((certificate) => certificate.id === id)?.name)
                .filter(Boolean),
            ...customCertificationNames,
        ].join(", ")

        const areasText = areasOfExpertise
            .filter((area) => area.fieldOfExpertiseId || area.experienceLevel || area.keySkillsIds.length || area.niceSkillsIds.length)
            .map((area, index) => {
                const skills = area.fieldOfExpertiseId ? (keySkillsByExpertise[String(area.fieldOfExpertiseId)] || []) : []
                const fieldName = fieldsOfExpertise.find((field) => field.id === area.fieldOfExpertiseId)?.name || ""
                const mustHaveSkills = formatSelectedNames(area.keySkillsIds, skills)
                const niceToHaveSkills = formatSelectedNames(area.niceSkillsIds, skills)
                return `Area ${index + 1}: Field of expertise: ${fieldName || "N/A"}; Years of experience: ${area.experienceLevel ?? "N/A"}; Must-have skills: ${mustHaveSkills || "N/A"}; Nice-to-have skills: ${niceToHaveSkills || "N/A"}.`
            })
            .join("\n")

        const educationText = educationRequirements
            .filter((requirement) => requirement.qualificationId || requirement.fieldOfStudyId || requirement.fieldOfStudyOtherName)
            .map((requirement, index) => {
                const qualificationName = qualifications.find((qualification) => qualification.id === requirement.qualificationId)?.name || ""
                const fieldOfStudyName =
                    requirement.fieldOfStudyOtherName ||
                    fieldsOfStudy.find((field) => field.id === requirement.fieldOfStudyId)?.name ||
                    ""
                return `Education ${index + 1}: Qualification: ${qualificationName || "N/A"}; Field of study: ${fieldOfStudyName || "N/A"}.`
            })
            .join("\n")

        const languageText = languageRequirements
            .filter((requirement) => requirement.languageId || requirement.speakingLevelId || requirement.readingWritingLevelId)
            .map((requirement, index) => {
                const languageName = languages.find((language) => language.id === requirement.languageId)?.name || ""
                const speakingLevel = speakingLevels.find((level) => level.value === requirement.speakingLevelId)?.label || requirement.speakingLevelId || ""
                const readingWritingLevel = readingWritingLevels.find((level) => level.value === requirement.readingWritingLevelId)?.label || requirement.readingWritingLevelId || ""
                return `Language ${index + 1}: Language: ${languageName || "N/A"}; Speaking level: ${speakingLevel || "N/A"}; Reading/writing level: ${readingWritingLevel || "N/A"}.`
            })
            .join("\n")

        const genderName = genders.find((gender) => gender.value === genderValue)?.label || genderValue || ""
        const visaStatusName = visaStatuses.find((status) => status.value === visaStatusValue)?.label || visaStatusValue || ""
        const drivingLicenseName = drivingLicenses.find((license) => license.value === drivingLicenseValue)?.label || drivingLicenseValue || ""

        return [
            `Job title: ${formData.jobTitle || "N/A"}`,
            `Department: ${departmentName || "N/A"}`,
            `Hiring manager: ${hiringManagerName || "N/A"}`,
            `Job level: ${jobLevelName || "N/A"}`,
            `Region: ${regionName || "N/A"}`,
            `Country: ${countryName || "N/A"}`,
            `City: ${cityName || "N/A"}`,
            `Employment type: ${employmentTypeName || "N/A"}`,
            `Workplace type: ${workplaceTypeName || "N/A"}`,
            `Nationality: ${
                nationalityCountries.find((c) => c.id === selectedNationalityId)?.name || "N/A"
            }`,
            `Opening date: ${formData.openingDate || "N/A"}`,
            `Closing date: ${formData.closingDate || "N/A"}`,
            `Industry experience: ${industryNames || "N/A"}`,
            areasText ? `Areas of expertise:\n${areasText}` : "Areas of expertise: N/A",
            `Management experience required: ${managementExperienceValue || "N/A"}`,
            `Management skill: ${
                managementExperienceValue === "YES"
                    ? peopleManagedRangeOptions.find((option) => option.id === peopleManagedRangeValue)?.name ||
                      "N/A"
                    : "N/A"
            }`,
            languageText ? `Language requirements:\n${languageText}` : "Language requirements: N/A",
            educationText ? `Educational requirements:\n${educationText}` : "Educational requirements: N/A",
            `Certification requirements: ${certificateNames || "N/A"}`,
            `Gender: ${genderName || "N/A"}`,
            `Valid visa: ${visaStatusName || "N/A"}`,
            `Driving license: ${drivingLicenseName || "N/A"}`,
        ].join("\n")
    }

    const handleJobDescriptionFocus = async () => {
        if (isGeneratingJobDescription) {
            return
        }

        const nextSourceText = buildJobDescriptionText()
        const shouldRegenerate =
            !tasks.trim() ||
            (hasGeneratedJobDescription && nextSourceText !== lastJobDescriptionSourceText)

        if (!shouldRegenerate) {
            setCanEditJobDescription(true)
            return
        }

        setIsGeneratingJobDescription(true)
        setCanEditJobDescription(false)
        setTasks("")
        try {
            if (!accessToken) {
                toast.error("Please log in again to generate the job description.")
                setCanEditJobDescription(true)
                return
            }

            const response = await fetchJobSummary(nextSourceText, accessToken)
            if (response.error) {
                toast.error(response.error)
                setCanEditJobDescription(true)
                return
            }

            setTasks(response.summary || "")
            setHasGeneratedJobDescription(true)
            setLastJobDescriptionSourceText(nextSourceText)
            setCanEditJobDescription(true)
        } catch (error) {
            console.error("Failed to generate job description:", error)
            toast.error("Failed to generate job description.")
            setCanEditJobDescription(true)
        } finally {
            setIsGeneratingJobDescription(false)
        }
    }

    const hasNonEmptyLevel = (value: string | null | undefined) =>
        typeof value === "string" && value.trim() !== ""

    const ensureJobDescription = async (): Promise<boolean> => {
        if (tasks.trim()) {
            return true
        }

        if (!accessToken) {
            toast.error("Please log in again to generate the job description.")
            return false
        }

        const nextSourceText = buildJobDescriptionText()
        setIsGeneratingJobDescription(true)
        setCanEditJobDescription(false)
        try {
            const response = await fetchJobSummary(nextSourceText, accessToken)
            if (response.error) {
                toast.error(response.error)
                return false
            }

            const summary = response.summary?.trim() || ""
            if (!summary) {
                toast.error("Failed to generate job description.")
                return false
            }

            setTasks(summary)
            setHasGeneratedJobDescription(true)
            setLastJobDescriptionSourceText(nextSourceText)
            setCanEditJobDescription(true)
            return true
        } catch (error) {
            console.error("Failed to generate job description:", error)
            toast.error("Failed to generate job description.")
            return false
        } finally {
            setIsGeneratingJobDescription(false)
        }
    }

    // Validate if all required fields are filled
    const isFormValid = useMemo(() => {
        // In edit mode, only validate editable fields
        if (isEditMode) {
            // Only check editable required fields.
            if (!selectedDepartmentId || !selectedHiringManagerId) {
                return false
            }
            if (!formData.openingDate || formData.openingDate.trim() === "") {
                return false
            }
            if (!formData.closingDate || formData.closingDate.trim() === "") {
                return false
            }
            if (!tasks || tasks.trim() === "") {
                return false
            }
            return true
        }

        // For create mode, check all required fields
        if (!selectedDepartmentId || !selectedHiringManagerId || !formData.jobTitle.trim() ||
            !selectedJobLevelId || !selectedRegionId || !selectedCountryId ||
            !selectedCityId || !selectedEmploymentTypeId || !selectedWorkplaceTypeId) {
            return false
        }

        // Check opening date
        if (!formData.openingDate || formData.openingDate.trim() === "") {
            return false
        }
        if (!formData.closingDate || formData.closingDate.trim() === "") {
            return false
        }

        // Check if at least one area of expertise is filled with both fieldOfExpertiseId and experienceLevel
        const hasValidAreaOfExpertise = areasOfExpertise.some(
            (area) => area.fieldOfExpertiseId !== null && area.experienceLevel != null
        )
        if (!hasValidAreaOfExpertise) {
            return false
        }

        const hasValidLanguageRequirement = languageRequirements.some(
            (req) =>
                req.languageId !== null &&
                hasNonEmptyLevel(req.speakingLevelId) &&
                hasNonEmptyLevel(req.readingWritingLevelId)
        )
        if (!hasValidLanguageRequirement) {
            return false
        }

        const hasValidEducationRequirement = educationRequirements.some((req) => {
            const hasQualification = req.qualificationId !== null
            const hasFieldOfStudy =
                req.fieldOfStudyId !== null ||
                (typeof req.fieldOfStudyOtherName === "string" && req.fieldOfStudyOtherName.trim().length > 0)
            return hasQualification && hasFieldOfStudy
        })
        if (!hasValidEducationRequirement) {
            return false
        }

        // Check driving license
        if (!drivingLicenseValue) {
            return false
        }

        // Check visa status
        if (!visaStatusValue) {
            return false
        }

        if (!managementExperienceValue) {
            return false
        }

        if (managementExperienceValue === "YES" && !peopleManagedRangeValue) {
            return false
        }

        return true
    }, [
        isEditMode,
        selectedDepartmentId,
        selectedHiringManagerId,
        formData.jobTitle,
        selectedJobLevelId,
        selectedRegionId,
        selectedCountryId,
        selectedCityId,
        selectedEmploymentTypeId,
        selectedWorkplaceTypeId,
        formData.openingDate,
        formData.closingDate,
        areasOfExpertise,
        languageRequirements,
        educationRequirements,
        drivingLicenseValue,
        visaStatusValue,
        managementExperienceValue,
        peopleManagedRangeValue,
        tasks,
    ])

    const handleSaveJob = async () => {
        if (!accessToken) {
            console.error("No access token available")
            return false
        }

        // Convert dates from "31 May 2025" format to "2025-05-31" format
        const convertDate = (dateStr: string): string => {
            try {
                const parsed = parse(dateStr, "d MMM yyyy", new Date())
                if (!isNaN(parsed.getTime())) {
                    return format(parsed, "yyyy-MM-dd")
                }
            } catch (error) {
                console.error("Date conversion error:", error)
            }
            return ""
        }

        // Build areas_of_expertise array
        const areasOfExpertisePayload = areasOfExpertise
            .filter((area) => area.fieldOfExpertiseId !== null)
            .map((area) => ({
                field_of_expertise: area.fieldOfExpertiseId!,
                years_in_this_area_of_expertise: area.experienceLevel !== null && area.experienceLevel !== undefined ? area.experienceLevel : 0,
                skills_must_have: area.keySkillsIds,
                skills_nice_to_have: area.niceSkillsIds,
            }))

        // Build education array
        const education = educationRequirements
            .filter((req) => req.qualificationId !== null || req.fieldOfStudyId !== null || req.fieldOfStudyOtherName)
            .map((req) => {
                const educationItem: any = {
                    qualification: req.qualificationId || 0,
                    field_of_study: req.fieldOfStudyId || null,
                }

                if (req.fieldOfStudyOtherName) {
                    educationItem.other_field_of_study = req.fieldOfStudyOtherName
                }

                return educationItem
            })

        // Build language_skills array
        const languageSkills = languageRequirements
            .filter((req) => req.languageId !== null)
            .map((req) => ({
                language: req.languageId!,
                speaking_level: req.speakingLevelId || "",
                reading_writing_level: req.readingWritingLevelId || "",
            }))

        // Convert string IDs to numbers if they're numeric, otherwise keep as string
        const convertIdToNumber = (id: string | null): number | string => {
            if (!id) return 0
            const num = Number(id)
            return isNaN(num) ? id : num
        }

        const buildManagementSkillsPayload = () => {
            if (managementExperienceValue !== "YES" || !peopleManagedRangeValue) {
                return []
            }

            const managementSkillId = Number(peopleManagedRangeValue)
            if (!Number.isFinite(managementSkillId)) {
                return []
            }

            return [
                {
                    management_skill: managementSkillId,
                    years_of_experience: managementYearsOfExperience,
                },
            ]
        }

        // Build payload
        let payload: any

        if (isEditMode) {
            // In edit mode, only send editable fields (Department, Hiring Manager, Opening Date, Closing Date, Description)
            // DO NOT include employment_type, workplace_type, or any other non-editable fields
            payload = {
                opening_date: convertDate(formData.openingDate),
                closing_date: convertDate(formData.closingDate),
                description: tasks,
                department: selectedDepartmentId || 0,
                hiring_manager: selectedHiringManagerId || 0,
            }
            // Explicitly ensure these fields are NOT included (safety check)
            if (payload.employment_type !== undefined) delete payload.employment_type
            if (payload.workplace_type !== undefined) delete payload.workplace_type

            // Log payload for debugging (remove in production if needed)
            console.log("Edit mode payload:", JSON.stringify(payload, null, 2))
        } else {
            // For create mode, send all fields
            payload = {
                opening_date: convertDate(formData.openingDate),
                closing_date: convertDate(formData.closingDate),
                description: tasks,
                department: selectedDepartmentId || 0,
                hiring_manager: selectedHiringManagerId || 0,
                job_title: formData.jobTitle.trim(),
                job_level: selectedJobLevelId || 0,
                region: selectedRegionId || 0,
                nationality: selectedNationalityId ? [selectedNationalityId] : [],
                city: selectedCityId || 0,
                employment_type: selectedEmploymentTypeId ? [convertIdToNumber(selectedEmploymentTypeId)] : [],
                workplace_type: selectedWorkplaceTypeId ? [convertIdToNumber(selectedWorkplaceTypeId)] : [],
                areas_of_expertise: areasOfExpertisePayload,
                education: education,
                language_skills: languageSkills,
                management_skills: buildManagementSkillsPayload(),
                certificate: certificationIds,
                industry: industryExp,
                status: "Open",
            }

            // Add optional fields only if they have values
            if (drivingLicenseValue) {
                payload.driving_license = drivingLicenseValue
            }
            if (visaStatusValue) {
                payload.visa_status = visaStatusValue
            }
            if (customCertificationNames.length > 0) {
                payload.other_certificates = customCertificationNames
            }
        }

        try {
            if (isEditMode && id) {
                // Update existing job
                const jobId = Number(id)
                const result = await updateJob(jobId, payload, accessToken)
                if (result.error) {
                    console.error("Failed to update job:", result.error)
                    toast.error(`Failed to update job: ${result.error}`)
                    return false
                } else {
                    toast.success("Job updated successfully!")
                    // Redirect to job overview after a short delay
                    setTimeout(() => {
                        navigate(`/jobs/${jobId}`)
                    }, 1500)
                    return true
                }
            } else {
                // Create new job
                const result = await createJob(payload, accessToken)
                if (result.error) {
                    console.error("Failed to save job:", result.error)
                    toast.error(`Failed to save job: ${result.error}`)
                    return false
                } else {
                    const jobData = result.data?.data || result.data
                    const jobId = jobData?.id
                    const companyNameFromApi =
                        jobData?.recruiter?.company?.name ??
                        jobData?.hiring_manager?.company?.name ??
                        ""
                    setFormData((prev) => ({ ...prev, companyName: companyNameFromApi }))

                    if (jobId) {
                        // Run matches for the created job
                        try {
                            const matchResult = await runJobMatches(jobId, accessToken)
                            if (matchResult.error) {
                                console.error("Failed to run job matches:", matchResult.error)
                                toast.error("Failed to run job matches")
                            } else {
                                const hardFiltersPayload = buildJobHardFiltersPayload({
                                    selectedEmploymentTypeId,
                                    selectedWorkplaceTypeId,
                                    selectedCityId,
                                    educationRequirements,
                                    languageRequirements,
                                    visaStatusValue,
                                    drivingLicenseValue,
                                    selectedNationalityId,
                                })

                                if (hasAnyJobHardFilterSelected(hardFiltersPayload)) {
                                    const hardFilterResult = await applyJobHardFilters(
                                        jobId,
                                        hardFiltersPayload,
                                        accessToken
                                    )
                                    if (hardFilterResult.error) {
                                        console.error("Failed to apply job hard filters:", hardFilterResult.error)
                                    }
                                }

                                const matchesCreated = matchResult.data?.matches_created || 0
                                const matchesUpdated = matchResult.data?.matches_updated || 0
                                toast.success(`Job saved successfully! Matches created: ${matchesCreated}, Matches updated: ${matchesUpdated}`)
                            }
                        } catch (matchError) {
                            console.error("Error running job matches:", matchError)
                            // Don't show error to user as job was created successfully
                            toast.success("Job saved successfully!")
                        }
                    } else {
                        console.warn("Job ID not found in response, skipping match generation")
                        toast.success("Job saved successfully!")
                    }
                    return true
                }
            }
        } catch (error) {
            console.error("Error saving job:", error)
            toast.error(`An error occurred while ${isEditMode ? "updating" : "saving"} the job.`)
            return false
        }
    }

    const updateField = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
    }

    const handleDepartmentChange = (value: string) => {
        if (value === "") {
            setSelectedDepartmentId(null)
        } else {
            setSelectedDepartmentId(Number(value))
        }
    }

    const handleHiringManagerChange = (value: string) => {
        if (value === "") {
            setSelectedHiringManagerId(null)
        } else {
            setSelectedHiringManagerId(Number(value))
        }
    }

    const handleJobTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateField("jobTitle", e.target.value)
    }

    const handleJobLevelChange = (value: string) => {
        if (value === "") {
            setSelectedJobLevelId(null)
        } else {
            setSelectedJobLevelId(Number(value))
        }
    }

    const handleRegionChange = (value: string) => {
        if (value === "") {
            setSelectedRegionId(null)
        } else {
            setSelectedRegionId(Number(value))
        }
    }

    const handleCountryChange = (value: string) => {
        if (value === "") {
            setSelectedCountryId(null)
        } else {
            setSelectedCountryId(Number(value))
        }
    }

    const handleCityChange = (value: string) => {
        if (value === "") {
            setSelectedCityId(null)
            updateField("city", "")
        } else {
            setSelectedCityId(Number(value))
            const city = cities.find((c) => c.id === Number(value))
            if (city) {
                updateField("city", city.name)
            }
        }
    }

    const handleEmploymentTypeChange = (value: string) => {
        if (value === "") {
            setSelectedEmploymentTypeId(null)
        } else {
            setSelectedEmploymentTypeId(value)
        }
    }

    const handleWorkplaceTypeChange = (value: string) => {
        if (value === "") {
            setSelectedWorkplaceTypeId(null)
            updateField("workplaceType", "")
        } else {
            setSelectedWorkplaceTypeId(value)
            const workplaceType = workplaceTypes.find((wt) => String(wt.id) === String(value))
            if (workplaceType) {
                updateField("workplaceType", workplaceType.name)
            }
        }
    }

    const handleNationalityChange = (value: string) => {
        if (value === "") {
            setSelectedNationalityId(null)
        } else {
            setSelectedNationalityId(Number(value))
        }
    }

    const selectedWorkplaceTypeName =
        formData.workplaceType ||
        workplaceTypes.find((wt) => String(wt.id) === String(selectedWorkplaceTypeId))?.name ||
        ""

    const handleFooterAction = async () => {
        if (isEditMode) {
            await handleSaveJob()
            return
        }

        const hasDescription = await ensureJobDescription()
        if (!hasDescription) {
            return
        }

        setIsPreviewOpen(true)
    }

    const handleCreateFromPreview = async () => {
        setIsCreatingJob(true)
        const hasDescription = await ensureJobDescription()
        if (!hasDescription) {
            setIsCreatingJob(false)
            return
        }
        const wasCreated = await handleSaveJob()
        setIsCreatingJob(false)
        if (wasCreated) {
            setIsPreviewOpen(false)
            navigate("/all-jobs")
        }
    }

    if (isLoadingJob) {
        return (
            <div className="max-w-5xl mx-auto p-6 bg-white min-h-screen font-sans flex items-center justify-center">
                <p className="text-sm text-gray-500">Loading job data...</p>
            </div>
        )
    }

    return (
        <div className="max-w-5xl mx-auto p-6 bg-white min-h-screen font-sans">
            <JobPreviewModal
                isOpen={isPreviewOpen}
                onCreate={handleCreateFromPreview}
                onModify={() => setIsPreviewOpen(false)}
                isCreating={isCreatingJob}
                data={{
                    jobTitle: formData.jobTitle,
                    companyName: formData.companyName,
                    city: formData.city,
                    workplaceType: selectedWorkplaceTypeName,
                    openingDate: formData.openingDate,
                    closingDate: formData.closingDate,
                    description: tasks,
                }}
            />
            {isOtherCertificationModalOpen && (
                <CreateJobTextModal
                    title="Add certification"
                    label="Certification name"
                    value={otherCertificationName}
                    placeholder="Enter certification name"
                    closeLabel="Close certification popup"
                    onChange={setOtherCertificationName}
                    onClose={handleCloseOtherCertificationModal}
                    onSubmit={handleAddOtherCertification}
                />
            )}
            {fieldOfStudyModalIndex !== null && (
                <CreateJobTextModal
                    title="Add field of study"
                    label="Field of study name"
                    value={otherFieldOfStudyName}
                    placeholder="Enter field of study"
                    closeLabel="Close field of study popup"
                    onChange={setOtherFieldOfStudyName}
                    onClose={handleCloseOtherFieldOfStudyModal}
                    onSubmit={handleAddOtherFieldOfStudy}
                />
            )}

            <InternalInformationSection
                departments={departments}
                departmentsStatus={departmentsStatus}
                hiringManagers={hiringManagers}
                hiringManagersStatus={hiringManagersStatus}
                selectedDepartmentId={selectedDepartmentId}
                selectedHiringManagerId={selectedHiringManagerId}
                isEditMode={isEditMode}
                onDepartmentChange={handleDepartmentChange}
                onHiringManagerChange={handleHiringManagerChange}
            />

            <GeneralInformationSection
                formData={formData}
                isEditMode={isEditMode}
                jobLevels={jobLevels}
                jobLevelsStatus={jobLevelsStatus}
                selectedJobLevelId={selectedJobLevelId}
                regionsFiltered={regions}
                regionsStatus={regionsStatus}
                selectedRegionId={selectedRegionId}
                countries={countries}
                countriesStatus={countriesStatus}
                selectedCountryId={selectedCountryId}
                cities={cities}
                citiesStatus={citiesStatus}
                selectedCityId={selectedCityId}
                employmentTypes={employmentTypes}
                employmentTypesStatus={employmentTypesStatus}
                selectedEmploymentTypeId={selectedEmploymentTypeId}
                workplaceTypes={workplaceTypes}
                workplaceTypesStatus={workplaceTypesStatus}
                selectedWorkplaceTypeId={selectedWorkplaceTypeId}
                nationalityCountries={nationalityCountries}
                nationalityCountriesStatus={nationalityCountriesStatus}
                selectedNationalityId={selectedNationalityId}
                onOpenCalendar={setOpenCalendar}
                onJobTitleChange={handleJobTitleChange}
                onJobLevelChange={handleJobLevelChange}
                onRegionChange={handleRegionChange}
                onCountryChange={handleCountryChange}
                onCityChange={handleCityChange}
                onEmploymentTypeChange={handleEmploymentTypeChange}
                onWorkplaceTypeChange={handleWorkplaceTypeChange}
                onNationalityChange={handleNationalityChange}
                calendarNode={openCalendar && (() => {
                    const getInitialDate = (): Date | undefined => {
                        try {
                            const dateStr = openCalendar === "opening" ? formData.openingDate : formData.closingDate
                            if (dateStr && dateStr.trim()) {
                                const parsed = parse(dateStr, "d MMM yyyy", new Date())
                                if (!isNaN(parsed.getTime())) {
                                    return parsed
                                }
                            }
                        } catch (error) {
                            console.error("Date parsing error:", error)
                        }
                        return undefined
                    }

                    const initialDate = getInitialDate()
                    const label = openCalendar === "opening" ? "Opening date" : "Closing date"

                    return (
                        <DatePicker
                            isOpen={true}
                            onClose={() => setOpenCalendar(null)}
                            onUpdate={(date) => {
                                if (date && !isNaN(date.getTime())) {
                                    try {
                                        const formattedDate = format(date, "d MMM yyyy")
                                        if (openCalendar === "opening") {
                                            updateField("openingDate", formattedDate)
                                            if (formData.closingDate) {
                                                const closingDateParsed = parse(formData.closingDate, "d MMM yyyy", new Date())
                                                if (!isNaN(closingDateParsed.getTime()) && isBefore(startOfDay(closingDateParsed), startOfDay(date))) {
                                                    updateField("closingDate", "")
                                                    toast.warning("Closing date cannot be less than opening date. Closing date has been cleared.")
                                                }
                                            }
                                        } else {
                                            if (formData.openingDate) {
                                                const openingDateParsed = parse(formData.openingDate, "d MMM yyyy", new Date())
                                                if (!isNaN(openingDateParsed.getTime()) && isBefore(startOfDay(date), startOfDay(openingDateParsed))) {
                                                    toast.warning("Closing date cannot be less than opening date. Please select a date on or after the opening date.")
                                                    return
                                                }
                                            }
                                            updateField("closingDate", formattedDate)
                                        }
                                    } catch (error) {
                                        console.error("Date formatting error:", error)
                                    }
                                }
                            }}
                            initialDate={initialDate}
                            label={label}
                        />
                    )
                })()}
            />

            {/* --- Professional Experience Section --- */}
            <section className="mb-10">
                <SectionHeader title="Professional experience" />

                <div className="mb-4">
                    <FormField label="Industry experience">
                        <MultiSelect
                            options={industries.map((ind) => ind.name)}
                            selected={industryExp.map((id) => {
                                const industry = industries.find((ind) => ind.id === id)
                                return industry ? industry.name : ""
                            }).filter(Boolean)}
                            onToggle={(val) => {
                                if (!isEditMode) {
                                    const industry = industries.find((ind) => ind.name === val)
                                    if (industry) {
                                        toggleIndustryItem(industry.id)
                                    }
                                }
                            }}
                            placeholder={industriesStatus === "loading" ? "Loading..." : "Select industries"}
                        />
                    </FormField>

                    {/* <FormField label="Team management experience">
                        <div className="relative">
                            <select className="w-full bg-[#F9F9F9] p-3 pr-10 rounded-md appearance-none border-none text-small">
                                <option>Not required</option>
                                <option>Required</option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                        </div>
                    </FormField> */}
                </div>
            </section>

            <AreasOfExpertiseSection
                isEditMode={isEditMode}
                areasOfExpertise={areasOfExpertise}
                fieldsOfExpertise={fieldsOfExpertise}
                fieldsOfExpertiseStatus={fieldsOfExpertiseStatus}
                selectedJobLevelId={selectedJobLevelId}
                keySkillsByExpertise={keySkillsByExpertise}
                keySkillsLoadingByExpertise={keySkillsLoadingByExpertise}
                globalKeySkillIds={globalKeySkillIds}
                globalNiceSkillIds={globalNiceSkillIds}
                getExperienceLevelOptions={getExperienceLevelOptions}
                addAreaOfExpertise={addAreaOfExpertise}
                removeAreaOfExpertise={removeAreaOfExpertise}
                handleAreaOfExpertiseChange={(index, field, value) =>
                    handleAreaOfExpertiseChange(index, field as "fieldOfExpertiseId" | "experienceLevel", value)
                }
                toggleKeySkill={toggleKeySkill}
                toggleNiceSkill={toggleNiceSkill}
            />

            <ManagementExperienceSection
                isEditMode={isEditMode}
                managementExperienceOptions={managementExperienceOptions}
                peopleManagedRangeOptions={peopleManagedRangeOptions}
                managementExperienceValue={managementExperienceValue}
                peopleManagedRangeValue={peopleManagedRangeValue}
                peopleManagedRangeLoading={managementSkillsStatus === "loading"}
                onManagementExperienceChange={handleManagementExperienceChange}
                onPeopleManagedRangeChange={handlePeopleManagedRangeChange}
            />

            <LanguageRequirementsSection
                isEditMode={isEditMode}
                languageRequirements={languageRequirements}
                languages={languages}
                languagesStatus={languagesStatus}
                speakingLevels={speakingLevels}
                speakingLevelsStatus={speakingLevelsStatus}
                readingWritingLevels={readingWritingLevels}
                readingWritingLevelsStatus={readingWritingLevelsStatus}
                addLanguageRequirement={addLanguageRequirement}
                removeLanguageRequirement={removeLanguageRequirement}
                handleLanguageRequirementChange={(index, field, value) =>
                    handleLanguageRequirementChange(index, field as "languageId" | "speakingLevelId" | "readingWritingLevelId", value)
                }
            />

            <EducationalRequirementsSection
                isEditMode={isEditMode}
                educationRequirements={educationRequirements}
                qualifications={qualifications}
                qualificationsStatus={qualificationsStatus}
                fieldsOfStudy={fieldsOfStudy}
                fieldsOfStudyStatus={fieldsOfStudyStatus}
                otherFieldOfStudyOption={otherFieldOfStudyOption}
                addEducationRequirement={addEducationRequirement}
                removeEducationRequirement={removeEducationRequirement}
                handleEducationRequirementChange={(index, field, value) =>
                    handleEducationRequirementChange(index, field as "qualificationId" | "fieldOfStudyId", value)
                }
                handleFieldOfStudyChange={handleFieldOfStudyChange}
            />

            <CertificationRequirementsSection
                certificates={certificates}
                certificatesStatus={certificatesStatus}
                certificationIds={certificationIds}
                customCertificationNames={customCertificationNames}
                otherCertificationOption={otherCertificationOption}
                isEditMode={isEditMode}
                onToggle={(value) => {
                    if (value === otherCertificationOption) {
                        setIsOtherCertificationModalOpen(true)
                        return
                    }

                    if (customCertificationNames.includes(value)) {
                        setCustomCertificationNames(customCertificationNames.filter((name) => name !== value))
                        return
                    }

                    const certificate = certificates.find((item) => item.name === value)
                    if (!certificate) return
                    if (certificationIds.includes(certificate.id)) {
                        setCertificationIds(certificationIds.filter((id) => id !== certificate.id))
                    } else {
                        setCertificationIds([...certificationIds, certificate.id])
                    }
                }}
            />

            <AdditionalRequirementsSection
                isEditMode={isEditMode}
                genders={genders}
                gendersStatus={gendersStatus}
                genderValue={genderValue}
                visaStatuses={visaStatuses}
                visaStatusesStatus={visaStatusesStatus}
                visaStatusValue={visaStatusValue}
                drivingLicenses={drivingLicenses}
                drivingLicensesStatus={drivingLicensesStatus}
                drivingLicenseValue={drivingLicenseValue}
                tasks={tasks}
                isGeneratingDescription={isGeneratingJobDescription}
                isDescriptionReadOnly={isGeneratingJobDescription || (!canEditJobDescription && !tasks.trim())}
                onGenderChange={handleGenderChange}
                onVisaStatusChange={handleVisaStatusChange}
                onDrivingLicenseChange={handleDrivingLicenseChange}
                onTasksChange={setTasks}
                onDescriptionFocus={handleJobDescriptionFocus}
            />

            <CreateJobFooter
                isEditMode={isEditMode}
                isFormValid={isFormValid}
                isCreatingJob={isCreatingJob}
                onAction={handleFooterAction}
            />
        </div>
    )
}

export default CreateJob
